import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  Check,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Home,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import BuildingIcon from "lucide-react/dist/esm/icons/building.js";
import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { PageLayout } from "@/components/layout/PageLayout";
import { PageTitle } from "@/components/ui/page-title";
import { NavigationSidebar } from "@/components/navigation";
import type { NavItem } from "@/components/navigation";
import { useSidebarState } from "@/components/navigation/hooks/useSidebarState";
import { SidebarBottomMenus } from "@/components/navigation/organisms/SidebarBottomMenus";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { useBuildingList } from "@/hooks/buildings/useBuildingList";
import { observer } from "mobx-react-lite";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { CONTROL_FIELD_CLASS } from "@/components/ui/control-scale";
import { useUpdateBuildingName } from "@/hooks/building/useUpdateBuildingName";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useScrollReset } from "@/hooks/useScrollReset";
import { LoadingSkeleton } from "@/components/ui/loading-skeleton";
import { formatBuildingAddress } from "@/utils/formatBuildingAddress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PermissionAwareButton } from "@/components/ui/permission-aware-button";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { useBuildingCostConsent } from "@/domains/billing/useBuildingCostConsent";
import { billingBlockMessage } from "@/domains/billing/messages";
import { useOrgProjectsOverview } from "@/hooks/projects/useOrgProjectsOverview";
import type { OrgProjectOverview } from "@/stores/organizations/repository";
import { BuildingProjectRows } from "@/components/building/BuildingProjectRows";
import { PortfolioBuildingCard } from "@/components/building/PortfolioBuildingCard";
import { portfolioLayoutFor, type PortfolioLayout } from "@/components/building/portfolioLayout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { filterBuildingsByQuery } from "@/components/building/filterBuildingsByQuery";
import { NewBuildingDialog } from "@/domains/buildings/components/form/NewBuildingDialog";
import { CreateProjectDialog } from "@/domains/projects/components/CreateProjectDialog";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

type SortField = "name" | "address" | "registry_code" | "net_area";
type SortDirection = "asc" | "desc";

/**
 * localStorage scope key for the buildings-list sidebar collapse state.
 * The in-building sidebar scopes its key per building id; this page has no
 * building, so it uses a fixed key of its own.
 */
const SIDEBAR_SCOPE = "buildings-list";

// The page's one call to action: a red outline on white rather than a solid red fill.
// #ef4129 is the brand red of the login page's left panel, a touch deeper than primary.
const ADD_BUILDING_BUTTON_CLASS =
  "border-[#ef4129] bg-white text-foreground hover:bg-[#ef4129]/5 hover:text-foreground";

const EmptyState = ({ onAddNew }: { onAddNew: () => void }) => {
  const { t } = useLanguage();
  const { canCreateBuildings, canChooseBuildingPlan, billingBlockFor } = usePermissions();
  // Billing can withhold this button from someone who holds the grant. Keep it
  // on screen and let it say why, rather than vanishing with no explanation.
  const billingReason = billingBlockMessage(billingBlockFor(PERMISSIONS.CREATE_BUILDINGS), t);
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-center">
        <BuildingIcon className="mx-auto mb-6 h-16 w-16 text-muted-foreground" />
        <h3 className="mb-3 text-xl font-semibold">{t.buildings.list.emptyTitle}</h3>
        <p className="mb-6 text-lg text-muted-foreground">{t.buildings.list.emptyDescription}</p>
        {(canCreateBuildings || canChooseBuildingPlan || billingReason) && (
          <PermissionAwareButton
            variant="outline"
            size="control"
            className={ADD_BUILDING_BUTTON_CLASS}
            onClick={onAddNew}
            requiredPermission={
              canChooseBuildingPlan ? "canChooseBuildingPlan" : "canCreateBuildings"
            }
            permissionMessage={billingReason ?? t.common.permissions.createBuildings}
          >
            <Plus className="mr-2" />
            {t.buildings.list.addNewBuilding}
          </PermissionAwareButton>
        )}
      </div>
    </div>
  );
};

// Sort affordance: every sortable column shows a permanently-visible indicator.
// The active column shows the directional up/down arrow; inactive sortable columns
// show a muted neutral up/down icon so the sort handle is discoverable at all times
// (per reporter feedback — not hover-only, not active-column-only). Size matches the
// standardized active arrow (`h-3 w-3`).
const SortIcon = ({
  field,
  activeField,
  direction,
}: {
  field: SortField;
  activeField: SortField;
  direction: SortDirection;
}) => {
  if (field !== activeField) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/50" />;
  }
  return direction === "asc" ? (
    <ArrowUp className="ml-1 inline h-3 w-3" />
  ) : (
    <ArrowDown className="ml-1 inline h-3 w-3" />
  );
};

// Header-cell styling shared by the sortable columns — mirrors the Issues page
// header (`px-3 py-1.5 text-[10px]` uppercase muted) instead of the taller shadcn
// default, with no filled background.
const SORTABLE_HEAD =
  "h-auto cursor-pointer select-none px-3 py-1.5 text-[10px] hover:text-foreground";

// The active and archived views render this same table and must share ONE fixed
// column grid so toggling between them causes zero horizontal shift: the table is
// `table-fixed` and every non-flexing column gets an identical fixed width on the
// header row (which defines the grid). Chevron 40px fits the 24px toggle + px-2;
// registry (192px) and net-area (128px) fit their widest header incl. sort icon
// ("Building Registry Code" renders 145px + 16px icon + 24px padding); actions
// (144px) fits the widest control — the labeled Restore button (104px + 24px
// padding) — so the icon-only trash on the active view sits in the same slot.
// Name + Address carry no width and split the remaining space equally on both
// views.
const COL_CHEVRON = "w-10";
const COL_REGISTRY = "w-48";
const COL_NET_AREA = "w-32";
const COL_ACTIONS = "w-36";

// The name column: either the inline-rename input or the building name with a
// hover-reveal edit pencil. Extracted so the row-render stays small.
interface BuildingNameCellProps {
  name: string;
  canEdit: boolean;
  isEditing: boolean;
  editName: string;
  onEditNameChange: (value: string) => void;
  onEditKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
  onStartEdit: () => void;
}

// Takes a single props object (destructured in the body, not the signature) so
// Lizard counts one parameter rather than one per field.
const BuildingNameCell = (props: BuildingNameCellProps) => {
  const {
    name,
    canEdit,
    isEditing,
    editName,
    onEditNameChange,
    onEditKeyDown,
    onSave,
    onCancel,
    onStartEdit,
  } = props;
  const { t } = useLanguage();
  if (isEditing) {
    return (
      <div className="edit-name-container flex items-center gap-2">
        <Input
          value={editName}
          onChange={(e) => onEditNameChange(e.target.value)}
          onKeyDown={onEditKeyDown}
          className="h-8 w-60"
        />
        <Button size="icon" className="action-button h-8 w-8" onClick={onSave}>
          <Save className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="action-button h-8 w-8" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <div className="group flex items-center gap-2">
      <BuildingIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span>{name}</span>
      {canEdit && (
        <PermissionAwareButton
          variant="ghost"
          size="icon"
          className="action-button h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
          onClick={onStartEdit}
          requiredPermission="canEditAllBuildings"
          permissionMessage={t.common.permissions.editBuildings}
        >
          <Pencil className="h-3.5 w-3.5" />
        </PermissionAwareButton>
      )}
    </div>
  );
};

// The trailing actions column: an archive (or restore, in the archived view)
// confirmation dialog. Extracted so the row-render stays small.
interface BuildingArchiveDialogProps {
  showArchived: boolean;
  /**
   * Billing's reason for withholding RESTORE, when that is what withheld it.
   * Only ever set in the archived view: un-archiving raises the subscription
   * quantity, while archiving lowers the bill and is never withheld for a
   * billing reason (see `canArchiveBuildings` in usePermissions).
   */
  billingReason?: string;
  onArchive: () => void;
  onRestore: () => void;
}

// The confirmation dialog body (title + description + actions), split out of
// BuildingArchiveDialog so neither function exceeds the method-length limit.
const BuildingArchiveDialogContent = (props: BuildingArchiveDialogProps) => {
  const { showArchived, onArchive, onRestore } = props;
  const { t } = useLanguage();
  return (
    <AlertDialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {showArchived ? t.buildings.restoreTitle : t.buildings.archiveTitle}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {showArchived ? t.buildings.restoreConfirm : t.buildings.archiveConfirm}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
        <AlertDialogAction
          onClick={showArchived ? onRestore : onArchive}
          className={
            showArchived
              ? "bg-primary text-primary-foreground hover:bg-primary/90"
              : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
          }
        >
          {showArchived ? t.buildings.restore : t.buildings.archive}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
};

// Single props-object param (destructured in the body) keeps Lizard's parameter
// count at one. The two triggers are deliberately dissimilar (labeled outline
// button vs. icon-only ghost) so restore can never be mistaken for the trash
// can: different shape, different color, and a word.
const BuildingArchiveDialog = (props: BuildingArchiveDialogProps) => {
  const { showArchived, billingReason, onArchive, onRestore } = props;
  const { t } = useLanguage();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {showArchived ? (
          <PermissionAwareButton
            variant="outline"
            size="sm"
            className="action-button h-8"
            requiredPermission="canRestoreBuildings"
            permissionMessage={billingReason ?? t.common.permissions.restoreBuildings}
          >
            <ArchiveRestore className="mr-1.5 h-4 w-4" />
            {t.buildings.restore}
          </PermissionAwareButton>
        ) : (
          <PermissionAwareButton
            variant="ghost"
            size="icon"
            className="action-button h-8 w-8 text-muted-foreground hover:text-destructive"
            requiredPermission="canArchiveBuildings"
            permissionMessage={t.common.permissions.archiveBuildings}
          >
            <Trash2 className="h-4 w-4" />
          </PermissionAwareButton>
        )}
      </AlertDialogTrigger>
      <BuildingArchiveDialogContent
        showArchived={showArchived}
        onArchive={onArchive}
        onRestore={onRestore}
      />
    </AlertDialog>
  );
};

// Full-page error panel for the buildings list. Extracted so the page component
// stays under the method-length limit.
interface BuildingsErrorStateProps {
  error: unknown;
  onRetry: () => void;
  onHome: () => void;
}

const BuildingsErrorState = (props: BuildingsErrorStateProps) => {
  const { error, onRetry, onHome } = props;
  const { t } = useLanguage();
  return (
    <>
      <PageTitle info="buildings" wrapperClassName="mb-8" data-tour="buildings-header">
        {t.buildings.myBuildings}
      </PageTitle>

      <div className="flex flex-col items-center justify-center p-8">
        <AlertTriangle className="mb-4 h-12 w-12 text-destructive" />
        <h2 className="mb-4 text-xl font-semibold">{t.buildings.list.errorTitle}</h2>
        <p className="mb-4 max-w-md text-center text-muted-foreground">
          {error instanceof Error ? error.message : t.buildings.list.failedToLoad}
        </p>
        <div className="flex gap-3">
          <Button onClick={onRetry}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t.buildings.list.tryAgain}
          </Button>
          <Button variant="outline" onClick={onHome}>
            {t.buildings.list.goHome}
          </Button>
        </div>
      </div>
    </>
  );
};

// Page title + "Add building" button, plus the first-run "no buildings yet" hint.
// Extracted so the page component stays under the method-length limit.
interface BuildingsListHeaderProps {
  showArchived: boolean;
  canCreate: boolean;
  canChoosePlan: boolean;
  /** Billing's reason for withholding Add New Building, when that is the cause. */
  billingReason?: string;
  showNoBuildingsHint: boolean;
  onAddNew: () => void;
}

const BuildingsListHeader = (props: BuildingsListHeaderProps) => {
  const { showArchived, canCreate, canChoosePlan, billingReason, showNoBuildingsHint, onAddNew } =
    props;
  const { t } = useLanguage();
  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <PageTitle info="buildings" data-tour="buildings-header">
          {showArchived ? t.buildings.myArchivedBuildings : t.buildings.myBuildings}
        </PageTitle>

        {!showArchived && (canCreate || canChoosePlan || billingReason) && (
          <PermissionAwareButton
            variant="outline"
            size="control"
            className={ADD_BUILDING_BUTTON_CLASS}
            onClick={onAddNew}
            data-tour="add-building-button"
            {...pageInfoTarget("buildings.add")}
            requiredPermission={canChoosePlan ? "canChooseBuildingPlan" : "canCreateBuildings"}
            permissionMessage={billingReason ?? t.common.permissions.createBuildings}
          >
            <Plus className="mr-1.5" />
            {t.buildings.list.addNewBuilding}
          </PermissionAwareButton>
        )}
      </div>

      {showNoBuildingsHint && (
        <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4">
          <p className="text-blue-700">
            {t.buildings.list.noBuildingsIntro}{" "}
            <button type="button" onClick={onAddNew} className="font-medium underline">
              {t.buildings.list.createNewLink}
            </button>{" "}
            {t.buildings.list.getStartedSuffix}
          </p>
        </div>
      )}
    </>
  );
};

// Minimal shape the table needs from a building row — keeps the table decoupled
// from the full MST building model.
interface BuildingRowData {
  id: string;
  name: string;
  address: string;
  registry_code?: string | null;
  net_area?: number | null;
  archived?: boolean | null;
  subscription_plan_activation_pending?: boolean;
}

interface BuildingsTableSort {
  field: SortField;
  direction: SortDirection;
  onChange: (field: SortField) => void;
}

interface BuildingsTableEdit {
  canEdit: boolean;
  editingId: string | null;
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
  onStart: (building: { id: string; name: string }) => void;
}

interface BuildingsTableArchive {
  showArchived: boolean;
  /** May the viewer act on this view's control — archive on the active view,
   *  restore on the archived one. */
  canArchive: boolean;
  /** Set when billing — not the role grant — is withholding RESTORE, so the
   *  control stays focusable and explains itself instead of disappearing. */
  billingReason?: string;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
}

// Everything a row needs to render its projects expansion panel. `null` means
// the feature is off (RPC unavailable, or the archived view) — the table then
// renders without chevrons; the archived view keeps the column's indent via
// `keepIndent` so names still align with the active view.
interface BuildingsProjectsPanel {
  byBuilding: Map<string, OrgProjectOverview[]>;
  isLoading: boolean;
  expandedIds: Set<string>;
  matchedProjectIds: Set<number>;
  onToggle: (id: string) => void;
  onProjectClick: (buildingId: string, project: OrgProjectOverview) => void;
  /** Opens the create-project flow pre-scoped to the building. Absent when the
   *  viewer cannot create projects. */
  onAddProject?: (buildingId: string) => void;
}

interface BuildingsTableProps {
  buildings: BuildingRowData[];
  sort: BuildingsTableSort;
  edit: BuildingsTableEdit;
  archive: BuildingsTableArchive;
  projectsPanel: BuildingsProjectsPanel | null;
  /** Render an empty leading cell when the panel is off (the archived view) so
   *  name-column indentation matches the chevroned active view. */
  keepIndent?: boolean;
  onRowClick: (e: React.MouseEvent, id: string) => void;
  formatArea: (area: number | null) => string;
}

interface BuildingRowProps {
  building: BuildingRowData;
  edit: BuildingsTableEdit;
  archive: BuildingsTableArchive;
  projectsPanel: BuildingsProjectsPanel | null;
  keepIndent?: boolean;
  onRowClick: (e: React.MouseEvent, id: string) => void;
  formatArea: (area: number | null) => string;
}

// The trailing actions cell content — the archive/restore dialog, shown only when
// the row isn't being renamed and the user may archive. Split out so BuildingRow
// stays under the method-length limit.
const BuildingRowActions = (props: {
  building: BuildingRowData;
  edit: BuildingsTableEdit;
  archive: BuildingsTableArchive;
}) => {
  const { building, edit, archive } = props;
  if (edit.editingId === building.id) return null;
  if (!archive.canArchive && !archive.billingReason) return null;
  return (
    <BuildingArchiveDialog
      showArchived={archive.showArchived}
      billingReason={archive.billingReason}
      onArchive={() => archive.onArchive(building.id)}
      onRestore={() => archive.onRestore(building.id)}
    />
  );
};

// The leading expand/collapse chevron cell. The `action-button` class makes the
// row-click guard in handleRowClick ignore clicks here, so toggling never
// navigates. Split out so BuildingRow stays under the method-length limit.
const BuildingExpandCell = (props: { buildingId: string; panel: BuildingsProjectsPanel }) => {
  const { buildingId, panel } = props;
  const { t } = useLanguage();
  const isExpanded = panel.expandedIds.has(buildingId);
  return (
    <TableCell className={`px-2 py-4 ${COL_CHEVRON}`}>
      <Button
        {...pageInfoTarget("buildings.expandProjects")}
        variant="ghost"
        size="icon"
        className="action-button h-6 w-6"
        aria-expanded={isExpanded}
        aria-label={
          isExpanded ? t.buildings.list.collapseProjects : t.buildings.list.expandProjects
        }
        onClick={() => panel.onToggle(buildingId)}
      >
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </Button>
    </TableCell>
  );
};

// A single building row. Extracted from BuildingsTable's map callback so neither
// that callback nor this row exceeds the method-length limit. A single props
// object keeps Lizard's parameter count at one.
export const BuildingRow = (props: BuildingRowProps) => {
  const { building, edit, archive, projectsPanel, keepIndent, onRowClick, formatArea } = props;
  const { t } = useLanguage();
  const activationPending = building.subscription_plan_activation_pending === true;
  const isExpanded = !activationPending && (projectsPanel?.expandedIds.has(building.id) ?? false);
  const onAddProject = projectsPanel?.onAddProject;
  return (
    <>
      {/* When expanded, the building row and its projects panel read as ONE
          group: the row's own bottom divider is dropped (no line between the
          building and its first project) and both rows share a ~2% gray tint.
          The group stays bounded by the previous row's divider above and the
          panel row's divider below. */}
      <TableRow
        className={cn(
          activationPending ? "text-muted-foreground" : "cursor-pointer hover:bg-[#f7f7f7]",
          isExpanded && "border-b-0 bg-[#fafafa]"
        )}
        aria-disabled={activationPending}
        onClick={activationPending ? undefined : (e) => onRowClick(e, building.id)}
      >
        {projectsPanel && !activationPending ? (
          <BuildingExpandCell buildingId={building.id} panel={projectsPanel} />
        ) : (
          (keepIndent || projectsPanel) && <TableCell className={`px-2 py-4 ${COL_CHEVRON}`} />
        )}
        <TableCell className="break-words px-3 py-4 text-sm font-medium">
          <BuildingNameCell
            name={building.name}
            canEdit={edit.canEdit}
            isEditing={edit.editingId === building.id}
            editName={edit.value}
            onEditNameChange={edit.onChange}
            onEditKeyDown={edit.onKeyDown}
            onSave={edit.onSave}
            onCancel={edit.onCancel}
            onStartEdit={() => edit.onStart(building)}
          />
          {activationPending && (
            <p role="status" className="mt-1 text-xs font-normal">
              {t.billing.plans.activationPending}
            </p>
          )}
        </TableCell>
        <TableCell className="break-words px-3 py-4 text-muted-foreground">
          {formatBuildingAddress(building.address)}
        </TableCell>
        <TableCell className="px-3 py-4 text-muted-foreground">
          {building.registry_code || "—"}
        </TableCell>
        <TableCell className="px-3 py-4 text-muted-foreground">
          {formatArea(building.net_area ?? null)}
        </TableCell>
        <TableCell className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
          <BuildingRowActions building={building} edit={edit} archive={archive} />
        </TableCell>
      </TableRow>
      {projectsPanel && isExpanded && (
        <BuildingProjectRows
          projects={projectsPanel.byBuilding.get(building.id) ?? []}
          isLoading={projectsPanel.isLoading}
          highlightedProjectIds={projectsPanel.matchedProjectIds}
          onProjectClick={(project) => projectsPanel.onProjectClick(building.id, project)}
          onAddProject={onAddProject ? () => onAddProject(building.id) : undefined}
          colSpan={6}
        />
      )}
    </>
  );
};

// The buildings list table — borderless header + row dividers matching the Issues
// page. Extracted so the page component stays under the method-length limit.
// The sortable column header row. Split out so BuildingsTable stays under the
// method-length limit.
const BuildingsTableHeader = (props: { sort: BuildingsTableSort; hasIndentColumn: boolean }) => {
  const { sort, hasIndentColumn } = props;
  const { t } = useLanguage();
  return (
    <TableHeader>
      <TableRow>
        {hasIndentColumn && <TableHead className={`h-auto px-2 py-1.5 ${COL_CHEVRON}`} />}
        <TableHead className={SORTABLE_HEAD} onClick={() => sort.onChange("name")}>
          {t.buildings.name}
          <SortIcon field="name" activeField={sort.field} direction={sort.direction} />
        </TableHead>
        <TableHead className={SORTABLE_HEAD} onClick={() => sort.onChange("address")}>
          {t.buildings.address}
          <SortIcon field="address" activeField={sort.field} direction={sort.direction} />
        </TableHead>
        <TableHead
          className={`${SORTABLE_HEAD} whitespace-nowrap ${COL_REGISTRY}`}
          onClick={() => sort.onChange("registry_code")}
        >
          {t.buildings.registryCode}
          <SortIcon field="registry_code" activeField={sort.field} direction={sort.direction} />
        </TableHead>
        <TableHead
          className={`${SORTABLE_HEAD} whitespace-nowrap ${COL_NET_AREA}`}
          onClick={() => sort.onChange("net_area")}
        >
          {t.buildings.netArea}
          <SortIcon field="net_area" activeField={sort.field} direction={sort.direction} />
        </TableHead>
        <TableHead className={`h-auto px-3 py-1.5 ${COL_ACTIONS}`} />
      </TableRow>
    </TableHeader>
  );
};

const BuildingsTable = (props: BuildingsTableProps) => {
  const { buildings, sort, edit, archive, projectsPanel, keepIndent, onRowClick, formatArea } =
    props;
  return (
    <Table className="table-fixed">
      <BuildingsTableHeader sort={sort} hasIndentColumn={!!projectsPanel || !!keepIndent} />
      <TableBody>
        {buildings.map((building) => (
          <BuildingRow
            key={building.id}
            building={building}
            edit={edit}
            archive={archive}
            projectsPanel={projectsPanel}
            keepIndent={keepIndent}
            onRowClick={onRowClick}
            formatArea={formatArea}
          />
        ))}
      </TableBody>
    </Table>
  );
};

// Pure area formatter — module-level so it doesn't count toward the page
// component's method length.
const formatArea = (area: number | null) => {
  if (area == null) return "—";
  return `${area.toLocaleString()} m²`;
};

// Per-field comparator selection — an exhaustive switch keeps each function's
// cyclomatic complexity trivial (the old if/else chain tripped Codacy's limit)
// without the dynamic object lookup its security rule objects to.
const comparatorFor = (field: SortField): ((a: BuildingRowData, b: BuildingRowData) => number) => {
  switch (field) {
    case "name":
      return (a, b) => a.name.localeCompare(b.name);
    case "address":
      // Sorts on the rendered form so the order matches the column's text —
      // a county-first row would otherwise sort under its county, not its street.
      return (a, b) =>
        formatBuildingAddress(a.address).localeCompare(formatBuildingAddress(b.address));
    case "registry_code":
      return (a, b) => (a.registry_code ?? "").localeCompare(b.registry_code ?? "");
    case "net_area":
      return (a, b) => (a.net_area ?? 0) - (b.net_area ?? 0);
  }
};

// Sort comparator for the buildings table, extracted from the page component.
const compareBuildings = (
  a: BuildingRowData,
  b: BuildingRowData,
  field: SortField,
  direction: SortDirection
): number => {
  const cmp = comparatorFor(field)(a, b);
  return direction === "desc" ? -cmp : cmp;
};

// Splits the building list into active / archived / currently-shown sets.
// Extracted so the page component stays under the method-length limit.
const partitionBuildings = (buildings: BuildingRowData[], showArchived: boolean) => {
  const active = buildings.filter((b) => !(b.archived ?? false));
  const archived = buildings.filter((b) => b.archived ?? false);
  return { active, archived, filtered: showArchived ? archived : active };
};

// Projects across the given buildings — the layout's second measure.
const countProjects = (
  byBuilding: Map<string, OrgProjectOverview[]>,
  buildings: BuildingRowData[]
): number => buildings.reduce((sum, b) => sum + (byBuilding.get(b.id)?.length ?? 0), 0);

// Memoized sort for the buildings table. Extracted so the page component stays
// under the method-length limit (the comparator itself lives in compareBuildings).
const useSortedBuildings = (
  buildings: BuildingRowData[],
  field: SortField,
  direction: SortDirection
) =>
  useMemo(
    () => [...buildings].sort((a, b) => compareBuildings(a, b, field, direction)),
    [buildings, field, direction]
  );

// Project browsing on the buildings list: the org-wide projects overview, the
// per-building expansion state, and the building+project search filter. `enabled`
// is false in the archived view. When the get_org_projects_overview RPC is not
// deployed yet (companion franklin-data-pipeline#172), `available` turns false
// and the page degrades to the plain table with name-only search.
const useProjectBrowsing = (buildings: BuildingRowData[], enabled: boolean) => {
  const overview = useOrgProjectsOverview();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // RPC building ids are numbers; building-list ids are strings — normalize once.
  const byBuilding = useMemo(() => {
    const map = new Map<string, OrgProjectOverview[]>();
    for (const row of overview.rows) {
      const key = String(row.buildingId);
      const list = map.get(key);
      if (list) list.push(row);
      else map.set(key, [row]);
    }
    return map;
  }, [overview.rows]);

  const available = enabled && !overview.error;
  useEffect(() => {
    if (overview.error && process.env.NODE_ENV === "development") {
      console.warn(
        "get_org_projects_overview unavailable — project browsing disabled:",
        overview.error
      );
    }
  }, [overview.error]);

  const filtered = useMemo(
    () =>
      filterBuildingsByQuery(
        buildings,
        available ? byBuilding : new Map<string, OrgProjectOverview[]>(),
        searchQuery
      ),
    [buildings, byBuilding, searchQuery, available]
  );

  const onToggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Buildings matched via a project are force-expanded so the match is visible;
  // manual toggles still apply to everything else.
  const effectiveExpandedIds = useMemo(
    () =>
      filtered.autoExpandIds.size === 0
        ? expandedIds
        : new Set([...expandedIds, ...filtered.autoExpandIds]),
    [expandedIds, filtered.autoExpandIds]
  );

  return {
    available,
    // RPC health regardless of `enabled` — the archived view uses this to keep
    // the chevron column's indent (empty cells) so names align with the active
    // view, without claiming project browsing is on.
    rpcAvailable: !overview.error,
    isLoading: overview.isLoading,
    byBuilding,
    searchQuery,
    setSearchQuery,
    visibleBuildings: filtered.visible,
    matchedProjectIds: filtered.matchedProjectIds,
    expandedIds: effectiveExpandedIds,
    onToggle,
    refetchOverview: overview.refetch,
  };
};

// The search box above the buildings table (Active and Archived alike). Right
// aligned — `ml-auto` on a max-width block — so it ends on the same edge as the
// "Add New Building" button above it and the toolbars on every other page.
// Extracted so the page component stays under the method-length limit.
const BuildingsSearchInput = (props: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const { value, onChange, placeholder } = props;
  return (
    <div className="relative w-full max-w-sm">
      <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(CONTROL_FIELD_CLASS, "pl-8")}
        {...pageInfoTarget("buildings.search")}
      />
    </div>
  );
};

// The card layouts have no column headers to sort by, so they get the same
// sort fields as a menu beside the search box.
const SORT_MENU_OPTIONS: Array<{
  field: SortField;
  direction: SortDirection;
  label: (t: ReturnType<typeof useLanguage>["t"]) => string;
}> = [
  { field: "name", direction: "asc", label: (t) => t.buildings.list.sortOptions.nameAsc },
  { field: "name", direction: "desc", label: (t) => t.buildings.list.sortOptions.nameDesc },
  { field: "address", direction: "asc", label: (t) => t.buildings.list.sortOptions.addressAsc },
  { field: "address", direction: "desc", label: (t) => t.buildings.list.sortOptions.addressDesc },
  {
    field: "registry_code",
    direction: "asc",
    label: (t) => t.buildings.list.sortOptions.registryAsc,
  },
  { field: "net_area", direction: "desc", label: (t) => t.buildings.list.sortOptions.areaDesc },
  { field: "net_area", direction: "asc", label: (t) => t.buildings.list.sortOptions.areaAsc },
];

const BuildingsSortMenu = (props: {
  field: SortField;
  direction: SortDirection;
  onChange: (field: SortField, direction: SortDirection) => void;
}) => {
  const { t } = useLanguage();
  const current =
    SORT_MENU_OPTIONS.find((o) => o.field === props.field && o.direction === props.direction) ??
    SORT_MENU_OPTIONS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="control"
          className="shrink-0 gap-1.5"
          aria-label={`${t.buildings.list.sortBy}: ${current.label(t)}`}
        >
          <ArrowUpDown className="opacity-60" />
          {current.label(t)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {SORT_MENU_OPTIONS.map((option) => {
          const selected = option === current;
          return (
            <DropdownMenuItem
              key={`${option.field}:${option.direction}`}
              role="menuitemradio"
              aria-checked={selected}
              onSelect={() => props.onChange(option.field, option.direction)}
              className="text-xs"
            >
              {option.label(t)}
              {selected && <Check className="ml-auto h-3.5 w-3.5" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface BuildingsCardListProps {
  buildings: BuildingRowData[];
  layout: Exclude<PortfolioLayout, "table">;
  entryPathFor: (buildingId: string) => string;
  edit: BuildingsTableEdit;
  archive: BuildingsTableArchive;
  projectsPanel: BuildingsProjectsPanel | null;
}

// The card layouts of the page — one card per building, in the building
// dashboard's project tile look. Project lists start open when the portfolio
// is small enough to read at a glance.
const BuildingsCardList = (props: BuildingsCardListProps) => {
  const { buildings, layout, entryPathFor, edit, archive, projectsPanel } = props;
  return (
    <div className="space-y-3" data-portfolio-layout={layout}>
      {buildings.map((building) => {
        const onAddProject = projectsPanel?.onAddProject;
        return (
          <PortfolioBuildingCard
            key={building.id}
            building={building}
            entryPath={entryPathFor(building.id)}
            edit={edit}
            archive={archive}
            projects={
              projectsPanel
                ? {
                    projects: projectsPanel.byBuilding.get(building.id) ?? [],
                    isLoading: projectsPanel.isLoading,
                    matchedProjectIds: projectsPanel.matchedProjectIds,
                    defaultExpanded: layout === "expanded",
                    showActivity: true,
                    onAddProject: onAddProject ? () => onAddProject(building.id) : undefined,
                  }
                : null
            }
          />
        );
      })}
    </div>
  );
};

// Builds the Active/Archived sidebar items. Extracted to keep the page component
// under the method-length limit.
const buildBuildingsNavItems = (args: {
  buildingsListLabel: string;
  archivedLabel: string;
  isLoading: boolean;
  activeCount: number;
  archivedCount: number;
  showArchived: boolean;
  onSelect: (archived: boolean) => void;
}): NavItem[] => {
  const {
    buildingsListLabel,
    archivedLabel,
    isLoading,
    activeCount,
    archivedCount,
    showArchived,
    onSelect,
  } = args;
  return [
    {
      id: "active",
      label: isLoading ? buildingsListLabel : `${buildingsListLabel} (${activeCount})`,
      icon: Home,
      isActive: !showArchived,
      onClick: () => onSelect(false),
    },
    {
      id: "archived",
      label: isLoading ? archivedLabel : `${archivedLabel} (${archivedCount})`,
      icon: Archive,
      // WHA-3430: gray out and disable when there are no archived buildings —
      // nothing to view, so clicking it would only show an empty state.
      isActive: showArchived,
      disabled: !isLoading && archivedCount === 0,
      onClick: () => onSelect(true),
    },
  ];
};

const Buildings = observer(() => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { currentOrganization, isReady: isOrgReady } = useOrganizationContext();
  const {
    items: buildingItems,
    isLoading: isBuildingsLoading,
    error,
    refetch,
  } = useBuildingList({ orgId: currentOrganization?.id });
  const isLoading = !isOrgReady || isBuildingsLoading;
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showArchived, setShowArchived] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  // Building whose empty expansion panel launched the create-project flow;
  // null when the dialog is closed. Conditional mount resets the form each time.
  const [addProjectBuildingId, setAddProjectBuildingId] = useState<string | null>(null);
  const [editingBuildingId, setEditingBuildingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const updateBuildingName = useUpdateBuildingName();
  const permissions = usePermissions();
  const buildingStore = useBuildingStore();

  // Billing can withhold the bill-INCREASING controls from someone who holds
  // the grant — in a read-only organisation, or one whose billing status could
  // not be read at all. Those controls stay on screen and say why; a user who
  // never held the grant still sees nothing, because a button they could never
  // use is noise.
  const createBillingReason = billingBlockMessage(
    permissions.billingBlockFor(PERMISSIONS.CREATE_BUILDINGS),
    t
  );
  // Archiving is NOT one of them: it lowers the bill, so it stays available to
  // a read-only organisation exactly as the server allows it. Only restoring
  // (un-archiving) raises the quantity again, so only it can be withheld.
  const restoreBillingReason = billingBlockMessage(
    permissions.billingBlockFor(PERMISSIONS.MODIFY_DATA),
    t
  );
  const { request: requestCostConsent, dialog: costConsentDialog } = useBuildingCostConsent();

  // Sidebar collapse state is lifted here (mirroring BuildingLayout) so the
  // logo can switch between the wordmark and the standalone mark.
  const { isCollapsed: isNavCollapsed, toggleCollapse: toggleNavCollapse } = useSidebarState({
    buildingId: SIDEBAR_SCOPE,
    defaultCollapsed: false,
  });

  useScrollReset();

  // Legacy deep links to the old /buildings/new page redirect here with ?new=1.
  // Open the create-building modal (gated on permission) and drop the param so
  // it doesn't re-open on close/refresh.
  const canCreateBuildings = permissions.canCreateBuildings || permissions.canChooseBuildingPlan;
  useEffect(() => {
    if (searchParams.get("new") === null || !isOrgReady || !canCreateBuildings) return;
    if (canCreateBuildings) setIsAddDialogOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canCreateBuildings, isOrgReady]);

  const handleArchiveBuilding = async (buildingId: string) => {
    if (!permissions.canArchiveBuildings) {
      toast({
        variant: "destructive",
        title: t.common.permissions.denied,
        description: t.common.permissions.archiveBuildings,
      });
      return;
    }

    try {
      await buildingStore.archiveBuilding(buildingId, true);
      toast({
        title: t.buildings.archived,
        description: t.buildings.movedToArchive,
      });
      void refetch();
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error archiving building:", error);
      }
      toast({
        variant: "destructive",
        title: t.common.error,
        description: t.buildings.archiveError,
      });
    }
  };

  const handleRestoreBuilding = async (buildingId: string) => {
    if (!permissions.canRestoreBuildings) {
      toast({
        variant: "destructive",
        title: t.common.permissions.denied,
        description: t.common.permissions.restoreBuildings,
      });
      return;
    }

    // Un-archiving raises the subscription quantity — it is an add as far as the
    // bill is concerned — so it carries the same cost agreement as creating one.
    if (!(await requestCostConsent({ mode: "restore", buildingId: Number(buildingId) }))) return;

    try {
      await buildingStore.archiveBuilding(buildingId, false);
      toast({
        title: t.buildings.restored,
        description: t.buildings.restoredFromArchive,
      });
      void refetch();
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error restoring building:", error);
      }
      toast({
        variant: "destructive",
        title: t.common.error,
        description: t.buildings.restoreError,
      });
    }
  };

  const startEditingName = (building: { id: string; name: string }) => {
    setEditingBuildingId(building.id);
    setEditName(building.name);
  };

  const cancelEditingName = () => {
    setEditingBuildingId(null);
    setEditName("");
  };

  const saveEditedName = async () => {
    if (!editingBuildingId || !editName.trim()) {
      return cancelEditingName();
    }

    try {
      await updateBuildingName.mutateAsync({
        buildingId: editingBuildingId,
        name: editName.trim(),
      });
      toast({
        title: t.common.success,
        description: t.buildings.nameUpdated,
      });
      cancelEditingName();
      refetch();
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error renaming building:", error);
      }
      toast({
        variant: "destructive",
        title: t.common.error,
        description: t.buildings.renameError,
      });
    }
  };

  const {
    active: activeBuildings,
    archived: archivedBuildings,
    filtered: filteredBuildings,
  } = partitionBuildings(buildingItems, showArchived);

  const sortedBuildings = useSortedBuildings(filteredBuildings, sortField, sortDirection);

  // Project browsing (expansion + search) is active-view only — the RPC's
  // coverage of archived buildings is unspecified.
  const projectBrowsing = useProjectBrowsing(sortedBuildings, !showArchived);

  // Clicking a project always enters that project's context on its dashboard,
  // as a building dashboard's project tile does. The route is Projects-only, so
  // BuildingLayout flips the sidebar to Projects view, and the `?project=`
  // param becomes the scope.
  const handleProjectClick = (buildingId: string, project: OrgProjectOverview) => {
    navigate(`/buildings/${buildingId}/project-dashboard?project=${project.projectId}`);
  };

  const projectsPanel: BuildingsProjectsPanel | null = projectBrowsing.available
    ? {
        byBuilding: projectBrowsing.byBuilding,
        isLoading: projectBrowsing.isLoading,
        expandedIds: projectBrowsing.expandedIds,
        matchedProjectIds: projectBrowsing.matchedProjectIds,
        onToggle: projectBrowsing.onToggle,
        onProjectClick: handleProjectClick,
        onAddProject: permissions.canCreateProjects ? setAddProjectBuildingId : undefined,
      }
    : null;

  // The archived view has no chevron column; keep its indent whenever the
  // active view shows chevrons, so building names align between the two views.
  const keepIndent = showArchived && projectBrowsing.rpcAvailable;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const buildingEntryPath = (buildingId: string) =>
    permissions.isAdmin ? `/buildings/${buildingId}` : `/buildings/${buildingId}/documents`;

  // Cards for a small portfolio, the table for a large one — decided on the
  // view's whole list, not the search results, so typing never re-lays it out.
  // While the projects are still loading the count is 0; the cards show
  // skeleton rows until it arrives, so nothing visibly flips.
  const layout = portfolioLayoutFor(
    filteredBuildings.length,
    showArchived ? 0 : countProjects(projectBrowsing.byBuilding, filteredBuildings)
  );

  const handleRowClick = (e: React.MouseEvent, buildingId: string) => {
    if (
      (e.target as HTMLElement).closest(".action-button") ||
      (e.target as HTMLElement).closest(".edit-name-container")
    ) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    navigate(buildingEntryPath(buildingId));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      saveEditedName();
    } else if (e.key === "Escape") {
      cancelEditingName();
    }
  };

  // Active/Archived selection lives in the left sidebar as nav items. Counts
  // are appended once the building list has loaded (mirrors the old tabs).
  const navItems = buildBuildingsNavItems({
    buildingsListLabel: t.buildings.sidebar.buildingsList,
    archivedLabel: t.buildings.list.archived,
    isLoading,
    activeCount: activeBuildings.length,
    archivedCount: archivedBuildings.length,
    showArchived,
    onSelect: setShowArchived,
  });

  const mainContent = (
    <>
      <BuildingsListHeader
        showArchived={showArchived}
        canCreate={permissions.canCreateBuildings}
        canChoosePlan={permissions.canChooseBuildingPlan}
        billingReason={createBillingReason}
        showNoBuildingsHint={!isLoading && buildingItems.length === 0}
        onAddNew={() => setIsAddDialogOpen(true)}
      />

      {isLoading ? (
        <LoadingSkeleton type="table" count={1} />
      ) : !filteredBuildings || filteredBuildings.length === 0 ? (
        <EmptyState
          onAddNew={() => (showArchived ? setShowArchived(false) : setIsAddDialogOpen(true))}
        />
      ) : (
        <>
          <div className="mb-4 flex items-center justify-end gap-2">
            <BuildingsSearchInput
              value={projectBrowsing.searchQuery}
              onChange={projectBrowsing.setSearchQuery}
              placeholder={
                showArchived
                  ? t.buildings.list.searchArchivedPlaceholder
                  : t.buildings.list.searchPlaceholder
              }
            />
            {layout !== "table" && (
              <BuildingsSortMenu
                field={sortField}
                direction={sortDirection}
                onChange={(field, direction) => {
                  setSortField(field);
                  setSortDirection(direction);
                }}
              />
            )}
          </div>
          {projectBrowsing.visibleBuildings.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              {t.buildings.list.noSearchResults}
            </p>
          ) : layout !== "table" ? (
            <BuildingsCardList
              buildings={projectBrowsing.visibleBuildings}
              layout={layout}
              entryPathFor={buildingEntryPath}
              edit={{
                canEdit: permissions.canEditAllBuildings,
                editingId: editingBuildingId,
                value: editName,
                onChange: setEditName,
                onKeyDown: handleKeyDown,
                onSave: saveEditedName,
                onCancel: cancelEditingName,
                onStart: startEditingName,
              }}
              archive={{
                showArchived,
                canArchive: showArchived
                  ? permissions.canRestoreBuildings
                  : permissions.canArchiveBuildings,
                billingReason: showArchived ? restoreBillingReason : undefined,
                onArchive: handleArchiveBuilding,
                onRestore: handleRestoreBuilding,
              }}
              projectsPanel={projectsPanel}
            />
          ) : (
            <BuildingsTable
              buildings={projectBrowsing.visibleBuildings}
              sort={{ field: sortField, direction: sortDirection, onChange: handleSort }}
              edit={{
                canEdit: permissions.canEditAllBuildings,
                editingId: editingBuildingId,
                value: editName,
                onChange: setEditName,
                onKeyDown: handleKeyDown,
                onSave: saveEditedName,
                onCancel: cancelEditingName,
                onStart: startEditingName,
              }}
              archive={{
                showArchived,
                canArchive: showArchived
                  ? permissions.canRestoreBuildings
                  : permissions.canArchiveBuildings,
                billingReason: showArchived ? restoreBillingReason : undefined,
                onArchive: handleArchiveBuilding,
                onRestore: handleRestoreBuilding,
              }}
              projectsPanel={projectsPanel}
              keepIndent={keepIndent}
              onRowClick={handleRowClick}
              formatArea={formatArea}
            />
          )}
        </>
      )}
    </>
  );

  return (
    <PageLayout showHeader={false} customPadding>
      <div className="flex h-screen w-full">
        <NavigationSidebar
          buildingId={SIDEBAR_SCOPE}
          items={navItems}
          logo={
            <FranklinLogo
              size="sm"
              expanded={!isNavCollapsed}
              showBorder={false}
              onClick={() => navigate("/buildings")}
              className="bg-transparent"
            />
          }
          footer={({ isCollapsed }) => <SidebarBottomMenus isCollapsed={isCollapsed} />}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={toggleNavCollapse}
        />
        <div className="flex-1 overflow-auto p-6">
          {error && !isLoading ? (
            <BuildingsErrorState
              error={error}
              onRetry={() => refetch()}
              onHome={() => navigate("/")}
            />
          ) : (
            mainContent
          )}
        </div>
      </div>
      <NewBuildingDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} />
      {/* Asked before an un-archive, which is an add as far as the bill goes. */}
      {costConsentDialog}
      {addProjectBuildingId != null && (
        <CreateProjectDialog
          open
          onOpenChange={(open) => {
            if (!open) setAddProjectBuildingId(null);
          }}
          buildingId={addProjectBuildingId}
          // The expansion panel reads get_org_projects_overview, which the
          // create mutation doesn't invalidate — refetch so the new project
          // appears in the panel immediately.
          onCreated={() => void projectBrowsing.refetchOverview()}
        />
      )}
    </PageLayout>
  );
});

export default Buildings;
